// Training programs granted to a user by their team positions.
//
// Team playlists share group-level userTypes (TeamConstants GROUP_TO_USER_TYPES:
// every "Team (FW-ERT-FLS)" program is userType TEAM), and assigning a position
// grants that role on the Occupant record. Role-only eligibility therefore
// shows EVERY program of that type, not just the ones the position assigns,
// and only on the occupant record that holds the membership. These helpers
// track the assigned programs themselves (by playlist pk) so training surfaces
// can show exactly what the user's positions assign.

const lastSegment = (value) => (value ? `${value}`.split('#').pop() : null)

// Entries: { pk, userType, teamPk } — one per program per team membership.
export const teamProgramEntries = (memberships, positionsByTeam) => {
    const entries = []
    ;(memberships || []).forEach((m) => {
        if (!m?.pk) return
        const wanted = lastSegment(m.positionId)
        if (!wanted) return
        const positions = positionsByTeam[m.pk] || []
        const position = positions.find((p) => lastSegment(p.sk) === wanted)
        ;(position?.trainingPrograms || []).forEach((tp) => {
            if (!tp?.pk) return
            entries.push({ pk: tp.pk, name: tp.name || null, userType: tp.userType || null, teamPk: m.pk })
        })
    })
    return entries
}

export const entriesForPosition = (position, teamPk) =>
    (position?.trainingPrograms || [])
        .filter((tp) => tp?.pk)
        .map((tp) => ({ pk: tp.pk, name: tp.name || null, userType: tp.userType || null, teamPk: teamPk || null }))

const normalizeName = (value) => String(value || '').trim().toLowerCase()
const idOf = (value) => (value ? `${value}`.split('#').pop() : '')

// Does this entry refer to this playlist? Positions store the playlist pk and
// title; match on either so a re-created playlist (new pk, same title) or a
// prefixed id still lines up.
export const entryMatchesPlaylist = (entry, playlist) => {
    if (!entry || !playlist) return false
    if (entry.pk && playlist.pk && idOf(entry.pk) === idOf(playlist.pk)) return true
    const title = normalizeName(playlist.meta?.title?.[0]?.text)
    return Boolean(title) && normalizeName(entry.name) === title
}

export const mergeTeamPrograms = (current, added) => {
    const seen = new Set((current || []).map((e) => `${e.teamPk}|${e.pk}`))
    const next = [...(current || [])]
    ;(added || []).forEach((e) => {
        const key = `${e.teamPk}|${e.pk}`
        if (seen.has(key)) return
        seen.add(key)
        next.push(e)
    })
    return next
}

export const removeTeamPrograms = (current, teamPk) =>
    (current || []).filter((e) => e.teamPk !== teamPk)
